import { useState, useEffect } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import { cartHistory, getEstimates, upsertCart } from "../../../../../services";
import { stock } from "../../../Core/stock";
import useSessionStore from "../../../../../stores/useSessionStore";
import useAuthStore from "../../../../../stores/useAuthStore";
import useAdvanceOrderStore from "../../../../../stores/useAdvanceOrderStore";
import useTransactionStore from "../../../../../stores/useTransactionStore";
import { generateDocumentNo } from "../../../../../utils/documentSequenceUtils";

/**
 * Hook for cart history (parked bills via API) and estimates retrieval.
 *
 * @param {Object} deps
 * @param {Object}   deps.cart
 * @param {Function} deps.setCart
 * @param {Object}   deps.tillData
 * @param {boolean}  deps.isParkedBill
 * @param {Function} deps.setIsparkedBill
 * @param {Function} deps.setShowPaymentMethods
 * @param {Function} deps.setOrderHistoryDetails
 * @param {Function} deps.setSelectedProductInCart
 * @param {Function} deps.setProductStock
 * @param {Function} deps.setTempCart
 * @param {Function} deps.setShowCompleteSaleStock
 * @param {Function} deps.setCompleteSaleStockData
 */
const useCartHistory = ({
  cart,
  setCart,
  tillData,
  isParkedBill,
  setIsparkedBill,
  setShowPaymentMethods,
  setOrderHistoryDetails,
  setSelectedProductInCart,
  setProductStock,
  setTempCart,
  setShowCompleteSaleStock,
  setCompleteSaleStockData,
  history,
}) => {
  // ── Cart history state ─────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("pending");
  const [loadingCart, setLoadingCart] = useState(false);
  const [cartHistoryData, setCartHistoryData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  // ── Estimates state ────────────────────────────────────────────────
  const [isEstimatesFlag, setIsEstimatesFlag] = useState(false);
  const [activeEstimatesTab, setActiveEstimatesTab] = useState("pending");
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  const [estimatesData, setEstimatesData] = useState([]);
  const [searchEstimatesInput, setSearchEstimatesInput] = useState("");
  const [expandedEstimatesRowKey, setExpandedEstimatesRowKey] = useState(null);

  // ── Helpers ────────────────────────────────────────────────────────

  const getCurrentMonthRange = () => {
    const startOfMonth = dayjs().startOf("month").format("YYYY-MM-DD");
    const endOfMonth = dayjs().endOf("month").format("YYYY-MM-DD");
    return [startOfMonth, endOfMonth];
  };

  const getStatusForTab = (tab) => {
    switch (tab) {
      case "pending":
        return "DR";
      case "processed":
        return "CO";
      case "cancelled":
        return "CL";
      default:
        return "DR";
    }
  };

  const getStatusForEstimatesTab = (tab) => {
    switch (tab) {
      case "pending":
        return "PE";
      case "processed":
        return "CO";
      case "cancelled":
        return "CL";
      default:
        return "PE";
    }
  };

  // ── Cart history ───────────────────────────────────────────────────

  const fetchCartHistory = async (status) => {
    setLoadingCart(true);
    try {
      const dateRange = getCurrentMonthRange();
      const filterBy = JSON.stringify({
        status: [status],
        DateRange: dateRange,
      });
      const sortBy = JSON.stringify({
        created: "DESC",
      });

      const response = await cartHistory({
        q: searchInput,
        filter_by: filterBy,
        sort_by: sortBy,
        tillId: tillData?.tillAccess?.cwrTill?.cwrTillID || null,
      });
      if (response?.status === 200) {
        const data = JSON.parse(response?.data?.data?.cartHistory || "[]");

        const transformedData = data.map((item, index) => ({
          ...item,
          key: item.cwr_store_discount_id || `${index}`,
        }));

        setCartHistoryData(transformedData);
      } else {
        message.warning("Something is wrong in cartHistory service");
      }
    } catch (error) {
      console.error("Error fetching cart history:", error);
    } finally {
      setLoadingCart(false);
    }
  };

  // Fetch on parked-bill open (default Pending/DR)
  useEffect(() => {
    if (isParkedBill) fetchCartHistory("DR");
  }, [isParkedBill]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    const status = getStatusForTab(key);
    fetchCartHistory(status);
    setExpandedRowKey(null);
  };

  const toggleExpand = (key) => {
    setExpandedRowKey(expandedRowKey === key ? null : key);
  };

  const handleRetrieve = async (record) => {
    console.debug("[v0] Retrieving record:", record);
    const selectedParkedBill = { ...record.cart_items, isRetrived: "Y", cwr_pos_cart_id: record?.cwr_pos_cart_id };
    let stockCheckResult = true;
    let isFinalValidation = false;
    let isCompleteSale = false;
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y") {
      stockCheckResult = await stock({
        cart: selectedParkedBill,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation,
        setTempCart,
        isCompleteSale: true,
        setShowCompleteSaleStock,
        setCompleteSaleStockData,
      });
      if (!stockCheckResult) {
        setSelectedProductInCart({});
        return;
      } else {
        setShowPaymentMethods(true);
        setOrderHistoryDetails([]);
        setIsparkedBill(false);
      }
    } else {
      setShowPaymentMethods(true);
      setOrderHistoryDetails([]);
      setIsparkedBill(false);
    }
    setCart(selectedParkedBill);
    setCartHistoryData([]);
    setExpandedRowKey(null);
  };

  // ── Estimates ──────────────────────────────────────────────────────

  const fetchEstimates = async (status) => {
    setLoadingEstimates(true);
    try {
      const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
      const currentDate = dayjs().format("YYYY-MM-DD");
      const response = await getEstimates({
        bunitId: bunitId,
        recordId: "",
        status: status,
        startRow: "0",
        endRow: "2000",
        date: currentDate,
        documentno: searchEstimatesInput,
      });
      if (response?.status === 200) {
        const data = JSON.parse(response?.data?.data?.getEstimates || "[]");

        const transformedData = data?.estimates?.map((item, index) => ({
          ...item,
          key: item.s_estimate_id || `${index}`,
        }));

        setEstimatesData(transformedData);
      } else {
        message.warning("Something is wrong in get estimates service");
      }
    } catch (error) {
      console.error("Error fetching estimates:", error);
    } finally {
      setLoadingEstimates(false);
    }
  };

  // Fetch on estimates open (default Pending/PE)
  useEffect(() => {
    if (isEstimatesFlag) fetchEstimates("PE");
  }, [isEstimatesFlag]);

  const handleRetrieveEstimate = async (record) => {
    console.debug("[v0] Retrieving estimate record:", record);
    const tillDocumentSequence = useSessionStore.getState().documentSequence;
    const cartData = JSON.parse(record.estimate_json || "{}");
    cartData.estimateId = record?.s_estimate_id;
    cartData.tillName = useAuthStore.getState().tillValue?.name;

    const isAdvanceOrder = record?.is_advanceorder === "Y";
    if (isAdvanceOrder) {
      cartData.layAway = "Y";
      cartData.isAdvanceOrder = "Y";
      useAdvanceOrderStore.getState().setIsAdvanceOrderEnabled(true);
      const preSeq = useSessionStore.getState().preorderSequence;
      if (preSeq?.prefix) {
        cartData.documentno = generateDocumentNo(preSeq);
      } else {
        cartData.documentno = `${tillData.tillAccess.cwrTill.prefix}${tillDocumentSequence + 1}`;
      }
    } else {
      cartData.documentno = `${tillData.tillAccess.cwrTill.prefix}${tillDocumentSequence + 1}`;
    }

    let stockCheckResult = true;
    let isFinalValidation = false;
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isAdvanceOrder) {
      stockCheckResult = await stock({
        cart: cartData,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation,
        setTempCart,
        isCompleteSale: true,
        setShowCompleteSaleStock,
        setCompleteSaleStockData,
      });
      if (!stockCheckResult) {
        setSelectedProductInCart({});
        return;
      } else {
        setShowPaymentMethods(true);
        setOrderHistoryDetails([]);
        setIsEstimatesFlag(false);
      }
    } else {
      setShowPaymentMethods(true);
      setOrderHistoryDetails([]);
      setIsEstimatesFlag(false);
    }
    if (cartData?.customer?.isB2bEnabled === "Y") {
      localStorage.setItem("isB2bEnabled", "true");
      useTransactionStore.getState().setIsB2bEnabled("true");
    }
    if (cartData?.customer?.customerAddressIds) {
      localStorage.setItem("customerAddressIds", JSON.stringify(cartData.customer.customerAddressIds));
    }
    setCart(cartData);
    if (isAdvanceOrder && posConfig.enablePreOrder === "Y") {
      history.push("/preorder");
    }
    setEstimatesData([]);
    setExpandedEstimatesRowKey(null);
    setSearchEstimatesInput("");
  };

  const handleCancelSale = async (record) => {
    try {
      setLoadingCart(true);
      const csBunitId = tillData?.tillAccess?.csBunit?.csBunitId;
      const cwrTillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
      const cartPayload = {
        csBunitId,
        cwrPosCartId: record?.cwr_pos_cart_id || null,
        cwrTillId,
        status: "CL",
        cartItems: record?.cart_items || {},
      };
      const response = await upsertCart(cartPayload);
      if (response?.status === 200 && response?.data?.data?.upsertCart?.status === "200") {
        message.success("Sale cancelled successfully");
        fetchCartHistory("DR");
      } else {
        message.warning("Cancel sale failed. Please try again.");
        setLoadingCart(false);
      }
    } catch (error) {
      message.error("Something went wrong while cancelling the sale");
      setLoadingCart(false);
    }
  };

  // ── Return ─────────────────────────────────────────────────────────
  return {
    // cart history state
    activeTab,
    setActiveTab,
    loadingCart,
    setLoadingCart,
    cartHistoryData,
    setCartHistoryData,
    searchInput,
    setSearchInput,
    expandedRowKey,
    setExpandedRowKey,

    // estimates state
    isEstimatesFlag,
    setIsEstimatesFlag,
    activeEstimatesTab,
    setActiveEstimatesTab,
    loadingEstimates,
    setLoadingEstimates,
    estimatesData,
    setEstimatesData,
    searchEstimatesInput,
    setSearchEstimatesInput,
    expandedEstimatesRowKey,
    setExpandedEstimatesRowKey,

    // helpers
    getCurrentMonthRange,
    getStatusForTab,
    getStatusForEstimatesTab,

    // functions
    fetchCartHistory,
    handleTabChange,
    toggleExpand,
    handleRetrieve,
    handleCancelSale,
    fetchEstimates,
    handleRetrieveEstimate,
  };
};

export default useCartHistory;
